/* Zoho CRM web-to-lead submission as a custom element. Zoho's own snippet is
   now fetch-based too, so this stays close to it on the wire — same endpoint,
   same hidden fields, same multipart FormData POST — while replacing alert()
   validation with inline field errors and the fixed-position splash with an
   inline status banner.

   The Zoho form this targets has captcha disabled. If captcha is ever turned
   back on in Zoho, note it is Zoho's own image captcha (an extra field plus a
   reload endpoint), not Google reCAPTCHA — it would need adding here. */

(function () {
  class EscZohoForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.form.dataset.bound === 'true') return;
      this.form.dataset.bound = 'true';

      this.status = this.querySelector('[data-zoho-status]');
      this.submitButton = this.querySelector('[data-zoho-submit]');

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.onInput.bind(this));
    }

    /* editing a flagged field clears its error as you type */
    onInput(event) {
      const field = event.target.closest('.esc-zoho__field');
      if (field) this.clearError(field);
    }

    clearError(field) {
      if (field) field.classList.remove('esc-zoho__field--error');
    }

    setError(field, message) {
      if (!field) return;
      field.classList.add('esc-zoho__field--error');
      const target = field.querySelector('[data-zoho-error]');
      if (target && message) target.textContent = message;
    }

    validate() {
      let firstInvalid = null;

      this.querySelectorAll('[data-zoho-required]').forEach((input) => {
        const field = input.closest('.esc-zoho__field');
        this.clearError(field);
        if (input.value.trim().length !== 0) return;
        this.setError(field, `${input.dataset.zohoLabel || 'This field'} is required.`);
        firstInvalid = firstInvalid || input;
      });

      this.querySelectorAll('[ftype="email"]').forEach((input) => {
        const value = input.value.trim();
        if (value.length === 0) return;
        const at = value.indexOf('@');
        const dot = value.lastIndexOf('.');
        if (at >= 1 && dot >= at + 2 && dot + 2 < value.length) return;
        const field = input.closest('.esc-zoho__field');
        this.setError(field, 'Please enter a valid email address.');
        firstInvalid = firstInvalid || input;
      });

      if (firstInvalid) {
        if (typeof firstInvalid.focus === 'function') firstInvalid.focus();
        firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return false;
      }
      return true;
    }

    showStatus(state, message) {
      if (!this.status) return;
      this.status.dataset.state = state;
      const text = this.status.querySelector('[data-zoho-status-text]');
      if (text) text.textContent = message;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.validate()) return;

      // Zoho's own snippet forwards submits to its analytics tracker when present
      if (typeof window._wfa_track !== 'undefined' && window._wfa_track.wfa_submit) {
        window._wfa_track.wfa_submit(event);
      }

      const formData = new FormData(this.form);

      // Zoho smart-URL passthrough: ?service=smarturl must reach the CRM
      const params = new URLSearchParams(window.location.search);
      if (params.get('service') === 'smarturl') formData.append('service', 'smarturl');

      this.submitButton.setAttribute('disabled', 'true');
      this.showStatus('', '');

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          body: formData,
          cache: 'no-cache',
        });

        let message = this.dataset.successMessage;
        try {
          const contentType = response.headers.get('Content-Type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            if (data && data.actionvalue) message = data.actionvalue;
          }
        } catch (e) {
          /* Zoho does not always answer with JSON — fall back to our own copy */
        }

        this.form.reset();
        this.showStatus('success', message);
        this.status.focus();
      } catch (error) {
        this.showStatus('error', this.dataset.errorMessage);
      } finally {
        this.submitButton.removeAttribute('disabled');
      }
    }
  }

  if (!customElements.get('esc-zoho-form')) {
    customElements.define('esc-zoho-form', EscZohoForm);
  }
})();
